/**
 * @file prescriptions.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-14
 * @modified 2026-09-14
 * @description How a prescription reads in a table — its status badge and
 *              whether it has been ordered past. Shared by the Prescriptions
 *              screen and the contact modal's Prescriptions panel, so the two
 *              can never disagree about which prescriptions are valid.
 */

/**
 * The status badge for a prescription. Archived wins over the date-based
 * status: an archived prescription is out of use whatever its dates say.
 * Class names are the global .status-badge variants.
 */
export function prescriptionStatusDisplay(rx) {
  if (rx?.is_archived) return { label: 'Archived', className: 'archived' };
  if (rx?.status === 'Valid') return { label: 'Valid', className: 'active' };
  return { label: 'Expired', className: 'draft' };
}

/** Is this prescription in use right now — valid and not archived? */
export const isPrescriptionInUse = (rx) => !rx?.is_archived && rx?.status === 'Valid';

/**
 * How far past its prescription a customer has ordered, if at all.
 *
 *   'total'   more than the whole prescribed tonnage — the serious case, red
 *   'period'  more than expected in at least one month, while still within
 *             the overall total — worth a look, amber
 *   null      within the prescription
 *
 * Total wins when both are true: over overall is the stronger statement.
 */
export function overPrescribedLevel(rx) {
  if (parseFloat(rx?.remaining_tonnes || 0) < 0) return 'total';
  if (rx?.over_in_any_period === true) return 'period';
  return null;
}

/** Ordered past it in either sense. */
export const isOverPrescribed = (rx) => overPrescribedLevel(rx) !== null;

/** The row class for each level — see Prescriptions.css. */
export function overPrescribedRowClass(rx) {
  const level = overPrescribedLevel(rx);
  if (level === 'total') return 'over-prescribed';
  if (level === 'period') return 'over-period';
  return '';
}

/** Why a prescription is flagged — the two levels say different things. */
export function overPrescribedTitle(rx) {
  return overPrescribedLevel(rx) === 'total'
    ? 'Customer has ordered more than the prescribed tonnage'
    : 'Customer has ordered more than expected in at least one period';
}
